import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { apiCall } from '@/lib/http/client';
import {
  getPersistedPropertySchemas,
  setPersistedPropertySchemas,
} from '@/lib/localStorage/persistedPropertySchemas';

import type { SubPropertySchema } from '../core/SchemaClient';

/**
 * Property type schema from backend API.
 * Represents metadata about a single property type including its field type,
 * validation rules, and display options.
 */
export interface PropertyTypeSchema {
  property_type_name: string;
  entity_type_name: string;
  /** Entity id of the ahoy_property_type record when the property was created
   *  via an event (user-defined org_* properties and kind-stamped overrides);
   *  null for pure schema-template properties. Consumers (e.g. GenericCrmList
   *  config resolve) filter on this to find custom properties. */
  entity_id?: string | null;
  label: string;
  field_type_name: string;
  description: string;
  is_required: boolean;
  is_unique_value: boolean;
  is_multi?: boolean;
  value_write_access: 'all' | 'internal' | 'none';
  value_read_access: 'all' | 'internal';
  value_update_access: 'all' | 'none';
  options?: Array<{ value: string; label: string }>;
  icon?: string | null;
  subproperties?: SubPropertySchema[];
  default_value?: string | number | object | null;
  /** Name of the companion property that holds the JSON Schema for this property.
   *  Present on `json` field types that validate against a companion `json_schema` property. */
  schema_property?: string | null;
  /** Field-type-specific config exposed by the schema API. For `formula` fields
   *  this carries `result_type` and `target_entity_type_property` (the sibling
   *  property naming the formula's target entity type). */
  field_type_config?: {
    result_type?: string;
    target_entity_type_property?: string;
    /** For `entity_type` fields: restricts selectable entity types to those
     *  whose `entity_kind` intersects this list (e.g. ['case','party','asset']). */
    entity_kinds?: string[];
    /** For `permissions` fields: entity types admitted as grant targets by NAME,
     *  OR'd with `entity_kinds` (e.g. the schema meta types on
     *  `ahoy_service_account.ahoy_permissions`). */
    entity_type_names?: string[];
    /** For `permissions` fields: entity types that may be granted reads but
     *  never writes (e.g. web visits on `ahoy_service_account.ahoy_permissions`). */
    write_excluded_entity_type_names?: string[];
  } | null;
  /** Rollup configuration when this is a rollup (derived) property; null/absent
   *  otherwise. Serialized from the backend PropertyType dataclass. Used to
   *  detect system-derived properties in timeline filtering. */
  rollup_config?: object | null;
  /** Formula expression when this is a calculated (derived) property; null/absent
   *  otherwise. Serialized from the backend PropertyType dataclass. */
  formula?: string | null;
}

/**
 * Map of property type names to their schemas for O(1) lookup.
 */
export interface PropertySchemaMap {
  [propertyTypeName: string]: PropertyTypeSchema;
}

/**
 * Hook to fetch and cache property type schemas for a CRM entity type.
 *
 * The data is cached per org/entityType for efficient reuse across components.
 * Schemas are also persisted to localStorage (via `initialData`) so that the
 * first render after a navigation/refresh has the correct labels available
 * synchronously instead of flashing the derived fallback while waiting on the
 * network. The query still refetches in the background to pick up changes.
 *
 * @param orgId - The organization ID
 * @param entityTypeName - The entity type name (e.g., 'ahoy_contact', 'ahoy_deal')
 * @returns Object containing schema map, loading state, and error state
 *
 * @example
 * const { schema, loading, error } = usePropertySchema(orgId, 'ahoy_contact');
 * const emailSchema = schema['ahoy_email'];
 * console.log(emailSchema.field_type_name); // 'email'
 */
const PROPERTY_SCHEMA_STALE_TIME_MS = 5 * 60 * 1000; // schema doesn't change often

/**
 * Canonical React Query key for one entity type's property-type schema.
 *
 * Every consumer of `/schemas/property-types/?entity_type_name=X` MUST use
 * this helper (mirrors `entityTypesQueryKey` for object-types) so all callers
 * — including imperative `queryClient.fetchQuery` prefetchers like
 * GenericCrmList's config resolve — share one cache entry instead of
 * re-fetching the same schema under bespoke keys.
 */
export function propertySchemaQueryKey(
  orgId: string | number,
  entityTypeName: string
) {
  return ['propertySchema', orgId, entityTypeName] as const;
}

/** Canonical fetcher for one entity type's property-type schema (raw array). */
export async function fetchPropertySchemas(
  orgId: string | number,
  entityTypeName: string
): Promise<PropertyTypeSchema[]> {
  const url = `/api/orgs/${orgId}/crm-events/schemas/property-types/?entity_type_name=${encodeURIComponent(entityTypeName)}`;

  // Route through apiCall (not raw fetchWithDefaults) so the centralized 403
  // handling — access-paused / org-deactivated / billing-required redirect +
  // stall — still fires for imperative callers like GenericCrmList's config
  // resolve. Without it a paused/billing-gated user would stay on the page
  // with a generic query error instead of being redirected to recovery.
  const result = await apiCall<PropertyTypeSchema[]>(url);
  // Tolerate a 204 (apiCall returns {}) by normalizing to an empty array —
  // downstream consumers assume an array.
  return Array.isArray(result) ? result : [];
}

export function usePropertySchema(
  orgId: string | number,
  entityTypeName: string
) {
  const query = useQuery({
    queryKey: propertySchemaQueryKey(orgId, entityTypeName),
    queryFn: () => fetchPropertySchemas(orgId, entityTypeName),
    enabled: !!orgId && !!entityTypeName,
    staleTime: PROPERTY_SCHEMA_STALE_TIME_MS,
    initialData: () => getPersistedPropertySchemas(orgId, entityTypeName),
    initialDataUpdatedAt: () => 0,
  });

  // Persist successful responses to localStorage so subsequent navigations
  // (and reloads) can render the correct column labels synchronously.
  useEffect(() => {
    if (!orgId || !entityTypeName || !query.data) {
      return;
    }
    setPersistedPropertySchemas(orgId, entityTypeName, query.data);
  }, [query.data, orgId, entityTypeName]);

  // Convert array to map by property_type_name for O(1) lookup
  const schema = useMemo(() => {
    const schemaMap: PropertySchemaMap = {};
    if (query.data) {
      for (const prop of query.data) {
        schemaMap[prop.property_type_name] = prop;
      }
    }
    return schemaMap;
  }, [query.data]);

  return {
    schema,
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
